import { Check, Close, Publish } from '@mui/icons-material';
import { Button, Chip, IconButton, TextField, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { modalManagerStore } from '@/features/ModalManagerStore';
import { colorStore } from '@/features/queueColors';
import { DraftTimelog } from '@/types';
import { MuiColors } from '@/types/colors';
import { formatDurationIso } from '@/utils/formatDurationIso';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { parseTimeExpression } from '@/utils/parseTimeInput';

import { PublishTargetMenu } from '../PublishTargetMenu';
import { useDraftTimelogStore } from '../../model';

import s from './DraftLogCard.module.scss';

type DraftLogCardProps = {
  draft: DraftTimelog;
};

type FormValues = {
  duration: string;
};

export const DraftLogCard = observer(({ draft }: DraftLogCardProps) => {
  const draftTimelogStore = useDraftTimelogStore();
  const [editTime, setEditTime] = useState(false);

  const duration = useMemo(
    () => formatDurationRussian(draft.durationMinutes),
    [draft.durationMinutes],
  );

  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: { duration },
  });

  const watchedDuration = watch('duration');
  const isDurationValid = useMemo(() => {
    if (!watchedDuration || !watchedDuration.trim()) return false;

    return Boolean(parseTimeExpression(watchedDuration));
  }, [watchedDuration]);

  const keyPrefix = draft.taskKey?.split('-')[0] ?? draft.queueKey ?? null;
  const color = keyPrefix ? colorStore.getColorByPrefix(keyPrefix) : undefined;
  const needsBorder = keyPrefix ? colorStore.needsBorder(keyPrefix) : false;

  const onSaveDuration = async (data: FormValues) => {
    const parsedDuration = parseTimeExpression(data.duration);

    if (!parsedDuration) {
      return;
    }

    await draftTimelogStore.updateDraft(draft.id, {
      duration: formatDurationIso(parsedDuration),
    });
    setEditTime(false);
  };

  const handleEditClose = () => {
    setEditTime(false);
    reset({ duration });
  };

  return (
    <div
      className={s.card}
      onClick={() => {
        if (!editTime) {
          modalManagerStore.open('editDraft', draft);
        }
      }}
    >
      <div
        className={s.card__color}
        style={{
          backgroundColor: color,
          ...(needsBorder && {
            border: '1px solid rgba(0, 0, 0, 0.2)',
            borderLeft: 'none',
          }),
        }}
      />
      <div className={s.card__wrapper}>
        <div className={s.card__header}>
          <Chip
            label="Черновик"
            size="small"
            sx={{
              fontSize: 9,
              height: 18,
              bgcolor: '#fef3c7',
              color: '#92400e',
              fontWeight: 600,
            }}
          />
          <div className={s.card__header_more}>
            <PublishTargetMenu
              onPublish={(target) => draftTimelogStore.publishDraft(draft.id, target)}
            >
              {(open) => (
                <Tooltip title={!draft.taskKey ? 'Укажите задачу для публикации' : 'Опубликовать'}>
                  <span>
                    <IconButton size="small" disabled={!draft.taskKey} onClick={open}>
                      <Publish sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </PublishTargetMenu>
          </div>
        </div>
        <h2 className={s.card__task}>
          {draft.taskKey ? (
            <span
              style={{
                color,
                ...(needsBorder && {
                  textShadow: '0 0 1px rgba(0, 0, 0, 0.5), 0 0 1px rgba(0, 0, 0, 0.5)',
                }),
              }}
            >
              {draft.taskKey}
            </span>
          ) : draft.queueKey ? (
            <span style={{ color: 'var(--secondary-color)' }}>{draft.queueKey}</span>
          ) : (
            <span style={{ color: 'var(--secondary-color)', fontStyle: 'italic' }}>Без задачи</span>
          )}
        </h2>
        <div className={s.card__body}>
          {(draft.taskTitle || draft.queueName) && (
            <p className={s['card__body-title']}>{draft.taskTitle ?? draft.queueName}</p>
          )}
          {draft.comment && (
            <div className={s['card__body-description']}>
              <pre>{draft.comment}</pre>
            </div>
          )}
          <div className={s.card__time}>
            {!editTime ? (
              <Button
                sx={{
                  padding: 0,
                  minWidth: '5px',
                  borderRadius: '6px',
                  color: MuiColors.secondaryColor,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditTime(true);
                }}
              >
                {duration}
              </Button>
            ) : (
              <React.Fragment>
                <Button
                  disabled={!isDurationValid}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubmit(onSaveDuration)();
                  }}
                  sx={{ minWidth: '14px', height: '14px', width: '14px' }}
                >
                  <Check sx={{ width: '14px', height: '14px' }} />
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClose();
                  }}
                  sx={{ minWidth: '14px', height: '14px', width: '14px' }}
                >
                  <Close sx={{ width: '14px', height: '14px', color: MuiColors.dangerColor }} />
                </Button>
                <Controller
                  control={control}
                  name="duration"
                  rules={{ required: 'Введите время' }}
                  render={({ field }) => {
                    const parsedDuration = parseTimeExpression(field.value);
                    const isParsed = Boolean(parsedDuration);

                    return (
                      <>
                        <TextField
                          {...field}
                          size="small"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSubmit(onSaveDuration)();
                            }

                            if (e.key === 'Escape') {
                              e.preventDefault();
                              handleEditClose();
                            }
                          }}
                          sx={{
                            height: '21px',
                            fontSize: '12px',
                            '& .MuiInputBase-input': {
                              width: '45px',
                              height: '21px',
                              fontSize: '12px',
                              padding: '0 3px',
                              '@media (max-width:1500px)': {
                                fontSize: '10px',
                              },
                            },
                            '@media (max-width:1500px)': {
                              fontSize: '10px',
                            },
                          }}
                        />
                        <div
                          className={clsx(s.card__parsed_time, {
                            [s.correct]: isParsed,
                            [s.incorrect]: !isParsed,
                          })}
                        >
                          <span>
                            {field.value
                              ? isParsed
                                ? formatDurationRussian(parsedDuration)
                                : '0ч'
                              : '0ч'}
                          </span>
                        </div>
                      </>
                    );
                  }}
                />
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
